// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ReportVO } from '@project/model/vo/report.vo';
import { ReportGenerationPropertyDef } from '@project/components/project/constants/report-generation-property-def';
import { ProjectFinalizationInfoVO } from '@project/model/vo/project.vo';
import { Dictionary } from 'typescript-collections';
import { CsvMode, CsvModeUtil, SelectProjectReportModel } from '../select-project-for-csv/select-project-report.model';
import { ReportIdDef } from '@project/components/project/constants/report-id-def';
import { ReportGenerationPropertyUtils } from '@project/util/report-generation-property-utils';
import { ProjectModel } from '@project/model/project.model';
import { UserModel } from '@user/model/user.model';
import { UserDataService } from '@user/service/user-data.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ReportDataService } from '@project/components/report/service/report-data.service';
import { ReportGenerationPropertyVO } from '../../article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ReportGenerationPropertySetTypesDef } from '@project/components/project/constants/report-generation-property-set-type-def';
import { AlertButton } from '@shared/alert/alertButton';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ProjectHelper } from '@project/helper/project.helper';
import { GenerationResponseVO } from '@project/manager/generation-response.vo';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { FileExtension, FileUtils } from '@shared/util/file-utils';
import { StringUtils } from '@shared/util/string-utils';
import { UserUtils } from '@user/service/user-utils';
import { ErrorUtils } from '@shared/util/error-utils';
import { Subscription } from 'rxjs';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { RepresentationService } from '../../../service/representation.service';
import { ProjectService } from '../../import-export/service/project.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { BackgroundTasksInfoService } from '@project/service/background-tasks-info.service';

export interface CSVColumn {
  id: string;
  propertyName: string;
  selected: boolean;
  visible: boolean;
  disabled?: boolean;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class SelectProjectReportService {
  private static readonly TIMER_DELAY: number = 15000;
  public static readonly LOCALES_FOR_DEFAULT_ENCODING: string[] = ['en_US', 'de_DE'];
  public static readonly LOCALE_PREFIX: string = 'selectProjectReportPopup.properties.';

  private _contentIdToGenerationProcessMap: Object = {};

  private reportIdToSubtypesMap: Dictionary<string, string[]> = null;

  public finalizeProjectMode: boolean = false;
  public projectFinalizationInfo: ProjectFinalizationInfoVO;

  public projectGroupIds: number[] = [];
  public projectIds: number[] = [];
  private _userSelectedTemplateIds: string[] = null;

  public get type(): string {
    return this.exportModel.type;
  }

  public set type(value: string) {
    this.exportModel.type = value;
    this.updatePrintSettingsAvailability();
    this.applyUserSettings();
  }

  public get multipleProjectReport(): boolean {
    return this.exportModel.multipleProjectReport;
  }

  public set multipleProjectReport(value: boolean) {
    this.exportModel.multipleProjectReport = value;
    this.applyUserSettings();
  }

  private ownerCustomNumberColumnVisible: boolean = true;

  private _properties: ReportGenerationPropertyVO[] = [];

  constructor(
    private exportModel: SelectProjectReportModel,
    private userDataService: UserDataService,
    private userModel: UserModel,
    private globalContainer: GlobalContainer,
    private projectModel: ProjectModel,
    private representationService: RepresentationService,
    private localeResourceManager: LocaleResourceManager,
    private reportDataService: ReportDataService,
    private alertHelper: AlertHelper,
    private projectHelper: ProjectHelper,
    private sceneManager: SceneManager,
    private overlayService: OverlayService,
    private projectService: ProjectService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private backgroundTasksInfoService: BackgroundTasksInfoService,
    private readonly wireContourService: WireContourService,
    private readonly containerService: ContainerService,
  ) {}

  public showDialog(type, finalizeProjectMode: boolean = false) {
    this.finalizeProjectMode = finalizeProjectMode;
    this.resetModel();
    this.type = type;
    this.initCSVColumns();
    this.reportIdToSubtypesMap = new Dictionary<string, string[]>();
    this.reportIdToSubtypesMap.setValue(ReportIdDef.PROJECT_3D_REPORT_ID, ReportGenerationPropertyUtils.PROJECT_3D_REPORT_SUBTYPES);
    this.reportDataService.getReportTemplates(this.projectModel.currentProjectType, this.localeResourceManager.currentLanguage.langKey).subscribe({
      next: (reports: ReportVO[]) => this.onProjectReportSuccess(reports),
      error: () => this.onProjectReportFault()
    });
    this.exportModel.isDisplayOfProjectStructureEnabled = this.projectHelper.isDisplayOfProjectStructureEnabled();

    this.exportModel.selectedCsvMode = CsvMode.Grouped;
  }

  public next(): void {
    this.exportModel.nextButtonEnabled = false;
    const properties: ReportGenerationPropertyVO[] = this.collectPropertiesForReportGeneration();
    this._properties = properties;

    if (this.exportModel.saveSettings) {
      this.saveReportGenerationProperties(properties);
    }

    if (!this.exportModel.isPrintSettingsAvailable) {
      this.addDefaultPrintSettingsProperties(properties);
    }

    if (this.type === ReportVO.TYPE_CSV && !this.multipleProjectReport) {
      // In case the csv report is generated for a single project the value of IS_ALL_IN_ONE_REPORT_PROPERTY is always true
      // (there are no multiple projects, only one)
      ReportGenerationPropertyUtils.setBooleanPropertyValue(
        properties,
        ReportGenerationPropertyDef.IS_ALL_IN_ONE_REPORT,
        true,
        ReportGenerationPropertySetTypesDef.DEFAULT
      );
    }

    if (this.finalizeProjectMode) {
      this.overlayService.requestOverlayShow(OverlayBackground.color);
      this.setPdfWireContours();
      this.representationService.scheduleUpdateForReportGeneration().subscribe({
        next: () => {
          this.overlayService.requestOverlayHide(OverlayBackground.color);
          this.continueFinalizationPreviewGeneration(properties);
          this.setDefaultWireContours();
        },
        error: (error) => {
          this.overlayService.requestOverlayHide(OverlayBackground.color);
          this.projectReportGeneratedFailure(error);
          this.setDefaultWireContours();
        }
      });
    } else {
      if (this.type === ReportVO.TYPE_PDF) {
        this.exportModel.pdfConfirm.emit();
        this.backgroundTasksInfoService.setTaskActive("pdf-export", true);
      } else {
        this.exportModel.csvConfirm.emit();
        this.backgroundTasksInfoService.setTaskActive("csv-export", true);
      }
      this.interceptGenerateReportEvent(properties);
    }
  }

  private setPdfWireContours() {
    for (const container of  this.containerService.enumerateArticleContainersInGlobal()) {
      this.wireContourService.createWireContourForPdf(container);
    }
  }

  private setDefaultWireContours() {
    for (const container of  this.containerService.enumerateArticleContainersInGlobal()) {
      this.wireContourService.createWireContour(container);
    }
  }

  private continueFinalizationPreviewGeneration(properties: ReportGenerationPropertyVO[]): void {
    this.exportModel.pdfConfirm.emit();
    this.projectComponentPresenter.showFinalizationPreviewApproval(
      this.exportModel.reports,
      properties,
      this.localeResourceManager.currentLanguage.langKey,
      this.projectFinalizationInfo
    );
  }

  private initCSVColumns(): void {
    const csvColumnsList: CSVColumn[] = [
      {
        id: 'positionColumn',
        propertyName: ReportGenerationPropertyDef.IS_POSITION_COLUMN_VISIBLE,
        selected: true,
        visible: this.exportModel.isDiscreteCsvReport,
        disabled: this.exportModel.isDiscreteCsvReport,
        label: 'selectProjectReportPopup.column.position',
      },
      {
        id: 'countColumn',
        propertyName: ReportGenerationPropertyDef.IS_COUNT_COLUMN_VISIBLE,
        selected: true,
        visible: true,
        label: 'selectProjectReportPopup.column.count',
      },
      {
        id: 'orderQuantityColumn',
        propertyName: ReportGenerationPropertyDef.IS_ORDER_QUANTITY_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        disabled: this.exportModel.isDiscreteCsvReport,
        label: 'selectProjectReportPopup.column.order_quantity',
      },
      {
        id: 'articleIdNumberColumn',
        propertyName: ReportGenerationPropertyDef.IS_ARTICLE_ID_NUMBER_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        label: 'selectProjectReportPopup.column.article.id.number',
      },
      {
        id: 'articleNumberColumn',
        propertyName: ReportGenerationPropertyDef.IS_ARTICLE_NUMBER_COLUMN_VISIBLE,
        selected: true,
        visible: true,
        label: 'selectProjectReportPopup.column.article.number',
      },
      {
        id: 'customNumberColumn',
        propertyName: ReportGenerationPropertyDef.IS_CUSTOM_ARTICLE_NUMBER_COLUMN_VISIBLE,
        selected: true,
        visible: true,
        label: 'selectProjectReportPopup.column.custom.article.number',
      },
      {
        id: 'ownerCustomNumberColumn',
        propertyName: ReportGenerationPropertyDef.IS_OWNER_CUSTOM_ARTICLE_NUMBER_COLUMN_VISIBLE,
        selected: true,
        visible: this.ownerCustomNumberColumnVisible,
        label: 'selectProjectReportPopup.column.custom.article.number.owner',
      },
      {
        id: 'eanNumberColumn',
        propertyName: ReportGenerationPropertyDef.IS_EAN_NUMBER_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        label: 'selectProjectReportPopup.column.ean.number',
      },
      {
        id: 'descriptionColumn',
        propertyName: ReportGenerationPropertyDef.IS_DESCRIPTION_COLUMN_VISIBLE,
        selected: true,
        visible: true,
        label: 'selectProjectReportPopup.column.description',
      },
      {
        id: 'colorColumn',
        propertyName: ReportGenerationPropertyDef.IS_COLOR_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        label: 'selectProjectReportPopup.column.color',
      },
      {
        id: 'crossSectionColumn',
        propertyName: ReportGenerationPropertyDef.IS_CROSS_SECTION_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        label: 'selectProjectReportPopup.column.cross.section',
      },
      {
        id: 'weightColumn',
        propertyName: ReportGenerationPropertyDef.IS_WEIGHT_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        label: 'selectProjectReportPopup.column.weight',
      },
      {
        id: 'lengthColumn',
        propertyName: ReportGenerationPropertyDef.IS_LENGTH_COLUMN_VISIBLE,
        selected: this.exportModel.isGroupedCsvReport,
        visible: this.exportModel.isGroupedCsvReport,
        label: 'selectProjectReportPopup.column.length',
      },
    ];
    if (this.exportModel.csvColumnsList) {
      csvColumnsList.forEach((c) => {
        const column = this.exportModel.csvColumnsList.find((item) => item.id === c.id);
        if (column) {
          c.selected = column.selected;
        }
      });
    }
    this.exportModel.csvColumnsList = csvColumnsList;
  }

  private updatePrintSettingsAvailability(): void {
    this.exportModel.isPrintSettingsAvailable = this.isPrintSettingsAvailable();
    this.exportModel.canSeeInternalComments = UserUtils.canSeeInternalComments(this.userModel.user);
  }

  private isPrintSettingsAvailable(): boolean {
    return !(this.type === ReportVO.TYPE_CSV || this.projectModel.currentProjectType === "MARKINGSYSTEM");
  }

  public applyUserSettings(): void {
    if (this.type === ReportVO.TYPE_PDF) {
      this.applyPDFReportSettings();
    } else {
      this.applyCSVReportSettings();
    }
  }

  private applyPDFReportSettings(): void {
    const setType: string = ReportGenerationPropertyUtils.getSetTypeByProjectType(this.projectModel.currentProjectType);
    const userProperties: ReportGenerationPropertyVO[] = ReportGenerationPropertyUtils.getPDFReportGenerationProperties(
      this.userModel.userProfile.reportGenerationProperties,
      setType
    );
    const defaultUserProperties: ReportGenerationPropertyVO[] = ReportGenerationPropertyUtils.getPDFReportGenerationProperties(
      this.userModel.userProfile.reportGenerationProperties,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    this.exportModel.paperSizeListSelected =
      PropertyUtils.getStringPropertyValue(userProperties, ReportGenerationPropertyDef.PAPER_SIZE) || this.exportModel.paperSizeKeys[0];
    this.exportModel.usedNumbersGroupSelected =
      PropertyUtils.getStringPropertyValue(userProperties, ReportGenerationPropertyDef.ORDER_NUMBERS) || this.exportModel.orderNumbersKeys[0];
    this.exportModel.includeRemarks = PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.REMARKS)
      ? PropertyUtils.getBooleanPropertyValue(userProperties, ReportGenerationPropertyDef.REMARKS)
      : true;
    if (UserUtils.canSeeInternalComments(this.userModel.user)) {
      this.exportModel.includeInternalRemarks = PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.REMARKS_INTERNAL)
        ? PropertyUtils.getBooleanPropertyValue(userProperties, ReportGenerationPropertyDef.REMARKS_INTERNAL)
        : true;
      this.exportModel.includeInternalOptions = PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.PROJECT_OPTIONS_INTERNAL)
        ? PropertyUtils.getBooleanPropertyValue(userProperties, ReportGenerationPropertyDef.PROJECT_OPTIONS_INTERNAL)
        : true;
    }
    this.exportModel.includeArticleNumberLong = PropertyUtils.containsProperty(defaultUserProperties, ReportGenerationPropertyDef.IS_ARTICLE_NUMBER_LONG_VISIBLE)
      ? PropertyUtils.getBooleanPropertyValue(defaultUserProperties, ReportGenerationPropertyDef.IS_ARTICLE_NUMBER_LONG_VISIBLE)
      : true;
    this.exportModel.includeDocumentVersion = PropertyUtils.containsProperty(defaultUserProperties, ReportGenerationPropertyDef.IS_DOCUMENT_VERSION_VISIBLE)
      ? PropertyUtils.getBooleanPropertyValue(defaultUserProperties, ReportGenerationPropertyDef.IS_DOCUMENT_VERSION_VISIBLE)
      : true;
    this.exportModel.includeProjectVersion = PropertyUtils.containsProperty(defaultUserProperties, ReportGenerationPropertyDef.IS_PROJECT_VERSION_VISIBLE)
      ? PropertyUtils.getBooleanPropertyValue(defaultUserProperties, ReportGenerationPropertyDef.IS_PROJECT_VERSION_VISIBLE)
      : true;
    this.exportModel.useIds = PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.USE_ID_NUMBERS)
      ? PropertyUtils.getBooleanPropertyValue(userProperties, ReportGenerationPropertyDef.USE_ID_NUMBERS)
      : false;
    this.exportModel.isBorderVisible = PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.IS_BORDER_VISIBLE)
      ? PropertyUtils.getBooleanPropertyValue(userProperties, ReportGenerationPropertyDef.IS_BORDER_VISIBLE)
      : true;
    if (PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.SELECTED_TEMPLATES)) {
      this._userSelectedTemplateIds = PropertyUtils.getStringPropertyValues(userProperties, ReportGenerationPropertyDef.SELECTED_TEMPLATES);
    }
  }

  private applyPDFReportSpecificSettings(): void {
    const setType: string = ReportGenerationPropertyUtils.getSetTypeByProjectType(this.projectModel.currentProjectType);
    const userProperties: ReportGenerationPropertyVO[] = ReportGenerationPropertyUtils.getPDFReportGenerationProperties(
      this.userModel.userProfile.reportGenerationProperties,
      setType
    );
    for (const template of this.exportModel.reports) {
      const reportScale: number = ReportGenerationPropertyUtils.getReportScale(userProperties, template.id, setType);
      template.scale =
        NumberUtils.isNumber(reportScale) && this.exportModel.scaleKeys.indexOf(reportScale) !== -1 ? reportScale : this.exportModel.scaleKeys[0];

      const availableReportSubtypes: string[] = this.getSubtypesByReportId(template.id);
      if (ArrayUtils.isNotEmpty(availableReportSubtypes)) {
        const reportSubtype: string = ReportGenerationPropertyUtils.getReportSubtype(userProperties, template.id, setType);
        template.subtype = reportSubtype && availableReportSubtypes.indexOf(reportSubtype) !== -1 ? reportSubtype : availableReportSubtypes[0];
      }
    }
  }

  private applyCSVReportSettings(): void {
    const userProperties: ReportGenerationPropertyVO[] = ReportGenerationPropertyUtils.getCSVReportGenerationProperties(
      this.userModel.userProfile.reportGenerationProperties
    );

    this.exportModel.selectedCsvMode = CsvMode.Grouped;
    if (PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.CSV_SELECTED_MODE)) {
      const mode: CsvMode = CsvModeUtil.toEnum(PropertyUtils.getStringPropertyValue(userProperties, ReportGenerationPropertyDef.CSV_SELECTED_MODE));
      if (mode !== undefined) {
        this.exportModel.selectedCsvMode = mode;
      }
    }

    for (const csvColumn of this.exportModel.csvColumnsList) {
      if (PropertyUtils.containsProperty(userProperties, csvColumn.propertyName)) {
        csvColumn.selected = PropertyUtils.getBooleanPropertyValue(userProperties, csvColumn.propertyName);
      }
    }

    this.exportModel.dataStructureGroupSelected = this.exportModel.dataStructureGroup[0];
    if (this.multipleProjectReport && PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.IS_ALL_IN_ONE_REPORT)) {
      if (PropertyUtils.getBooleanPropertyValue(userProperties, ReportGenerationPropertyDef.IS_ALL_IN_ONE_REPORT)) {
        this.exportModel.dataStructureGroupSelected = this.exportModel.dataStructureGroup[1];
      }
    }
    this.dataStructureGroupChange(this.exportModel.dataStructureGroupSelected);

    if (PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.CSV_SELECTED_TEMPLATES)) {
      this._userSelectedTemplateIds = PropertyUtils.getStringPropertyValues(userProperties, ReportGenerationPropertyDef.CSV_SELECTED_TEMPLATES);
    }
    if (PropertyUtils.containsProperty(userProperties, ReportGenerationPropertyDef.SELECTED_ENCODING)) {
      this.exportModel.encodinGroupSelected = PropertyUtils.getStringPropertyValues(userProperties, ReportGenerationPropertyDef.SELECTED_ENCODING)[0];
    } else {
      if (SelectProjectReportService.LOCALES_FOR_DEFAULT_ENCODING.indexOf(this.localeResourceManager.currentLanguage.langKey) > -1) {
        this.exportModel.encodinGroupSelected = this.exportModel.encodingKeys[0];
      } else {
        this.exportModel.encodinGroupSelected = this.exportModel.encodingKeys[1];
      }
    }
  }

  private getSubtypesByReportId(reportId: string): string[] {
    if (!reportId) {
      return null;
    }
    return this.reportIdToSubtypesMap.containsKey(reportId) ? this.reportIdToSubtypesMap.getValue(reportId) : null;
  }

  private collectPropertiesForReportGeneration(): ReportGenerationPropertyVO[] {
    const properties: ReportGenerationPropertyVO[] = [];
    if (this.type === ReportVO.TYPE_PDF) {
      this.addPDFProperties(properties);
    } else {
      this.addCSVProperties(properties);
    }
    return properties;
  }

  private addPDFProperties(properties: ReportGenerationPropertyVO[]): void {
    const setType: string = ReportGenerationPropertyUtils.getSetTypeByProjectType(this.projectModel.currentProjectType);
    if (this.projectModel.currentProjectType !== "MARKINGSYSTEM") {
      ReportGenerationPropertyUtils.setStringPropertyValue(
        properties,
        ReportGenerationPropertyDef.PAPER_SIZE,
        this.exportModel.paperSizeListSelected,
        setType
      );
      ReportGenerationPropertyUtils.setStringPropertyValue(
        properties,
        ReportGenerationPropertyDef.ORDER_NUMBERS,
        this.exportModel.usedNumbersGroupSelected,
        setType
      );
      ReportGenerationPropertyUtils.setBooleanPropertyValue(
        properties,
        ReportGenerationPropertyDef.REMARKS,
        this.exportModel.includeRemarks,
        setType
      );
      if (UserUtils.canSeeInternalComments(this.userModel.user)) {
        ReportGenerationPropertyUtils.setBooleanPropertyValue(
          properties,
          ReportGenerationPropertyDef.REMARKS_INTERNAL,
          this.exportModel.includeInternalRemarks,
          setType
        );
        ReportGenerationPropertyUtils.setBooleanPropertyValue(
          properties,
          ReportGenerationPropertyDef.PROJECT_OPTIONS_INTERNAL,
          this.exportModel.includeInternalOptions,
          setType
        );
      } else {
        ReportGenerationPropertyUtils.setBooleanPropertyValue(properties, ReportGenerationPropertyDef.REMARKS_INTERNAL, false, setType);
        ReportGenerationPropertyUtils.setBooleanPropertyValue(properties, ReportGenerationPropertyDef.PROJECT_OPTIONS_INTERNAL, false, setType);
      }
      ReportGenerationPropertyUtils.setBooleanPropertyValue(
        properties,
        ReportGenerationPropertyDef.USE_ID_NUMBERS,
        this.projectHelper.isDisplayOfProjectStructureEnabled() ? this.exportModel.useIds : false,
        setType,
        true,
        !this.projectHelper.isDisplayOfProjectStructureEnabled()
      );
      ReportGenerationPropertyUtils.setBooleanPropertyValue(
        properties,
        ReportGenerationPropertyDef.IS_BORDER_VISIBLE,
        this.projectHelper.isDisplayOfProjectStructureEnabled() ? this.exportModel.isBorderVisible : true,
        setType,
        true,
        !this.projectHelper.isDisplayOfProjectStructureEnabled()
      );
      this.setExportModel(properties, setType);
    }
    ReportGenerationPropertyUtils.setBooleanPropertyValue(
      properties,
      ReportGenerationPropertyDef.IS_ARTICLE_NUMBER_LONG_VISIBLE,
      this.exportModel.includeArticleNumberLong,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    ReportGenerationPropertyUtils.setBooleanPropertyValue(
      properties,
      ReportGenerationPropertyDef.IS_DOCUMENT_VERSION_VISIBLE,
      this.exportModel.includeDocumentVersion,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    ReportGenerationPropertyUtils.setBooleanPropertyValue(
      properties,
      ReportGenerationPropertyDef.IS_PROJECT_VERSION_VISIBLE,
      this.exportModel.includeProjectVersion,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    this.addSelectedPDFTemplatesProperty(properties, setType);
  }

  private setExportModel(properties: ReportGenerationPropertyVO[], setType: string) {
    for (const report of this.exportModel.reports) {
      if (this.isReportScaleSelectionAvailable(report)) {
        ReportGenerationPropertyUtils.setIntPropertyValue(
          properties,
          ReportGenerationPropertyDef.SCALE,
          report.scale,
          setType,
          true,
          false,
          report.id
        );
      }
      if (report.subtype) {
        ReportGenerationPropertyUtils.setStringPropertyValue(
          properties,
          ReportGenerationPropertyDef.SUBTYPE,
          report.subtype,
          setType,
          true,
          false,
          report.id
        );
      }
    }
  }

  private addSelectedPDFTemplatesProperty(properties: ReportGenerationPropertyVO[], setType: string): void {
    const reportIds: string[] = this.getAllReportIds();
    const selectedReportIds: string[] = this.getSelectedReportIds();
    const selectedReportIdsFromOtherSets: string[] = !this._userSelectedTemplateIds
      ? null
      : this._userSelectedTemplateIds.filter(ArrayUtils.notInArray, reportIds);
    if (selectedReportIdsFromOtherSets != null && selectedReportIdsFromOtherSets.length > 0) {
      ArrayUtils.mergeArrays(selectedReportIds, selectedReportIdsFromOtherSets);
    }
    ReportGenerationPropertyUtils.setStringPropertyValues(properties, ReportGenerationPropertyDef.SELECTED_TEMPLATES, selectedReportIds, setType);
  }

  private addCSVProperties(properties: ReportGenerationPropertyVO[]): void {
    if (this.multipleProjectReport) {
      ReportGenerationPropertyUtils.setBooleanPropertyValue(
        properties,
        ReportGenerationPropertyDef.IS_ALL_IN_ONE_REPORT,
        this.exportModel.dataStructureGroupSelected === this.exportModel.dataStructureGroup[1],
        ReportGenerationPropertySetTypesDef.DEFAULT
      );
    }

    ReportGenerationPropertyUtils.setStringPropertyValues(
      properties,
      ReportGenerationPropertyDef.CSV_SELECTED_TEMPLATES,
      this.getSelectedReportIds(),
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    ReportGenerationPropertyUtils.setStringPropertyValue(
      properties,
      ReportGenerationPropertyDef.SELECTED_ENCODING,
      this.exportModel.encodinGroupSelected,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    ReportGenerationPropertyUtils.setStringPropertyValue(
      properties,
      ReportGenerationPropertyDef.CSV_SELECTED_MODE,
      this.exportModel.selectedCsvMode,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );

    for (const csvColumn of this.exportModel.csvColumnsList) {
      if (csvColumn.visible) {
        ReportGenerationPropertyUtils.setBooleanPropertyValue(
          properties,
          csvColumn.propertyName,
          csvColumn.selected,
          ReportGenerationPropertySetTypesDef.DEFAULT
        );
      }
    }
  }

  private addDefaultPrintSettingsProperties(properties: ReportGenerationPropertyVO[]): void {
    ReportGenerationPropertyUtils.setStringPropertyValue(
      properties,
      ReportGenerationPropertyDef.PAPER_SIZE,
      'a4',
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    ReportGenerationPropertyUtils.setStringPropertyValue(
      properties,
      ReportGenerationPropertyDef.ORDER_NUMBERS,
      'wago_numbers',
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
    ReportGenerationPropertyUtils.setBooleanPropertyValue(
      properties,
      ReportGenerationPropertyDef.REMARKS,
      true,
      ReportGenerationPropertySetTypesDef.DEFAULT
    );
  }

  private interceptGenerateReportEvent(properties: ReportGenerationPropertyVO[]): void {
    this.alertHelper
      .showAlert(
        this.type === ReportVO.TYPE_PDF ? 'main.title.generate_report_continue' : 'main.title.generate_csv_continue',
        'main.message.generate_report_continue',
        null,
        [AlertButton.YES, AlertButton.NO],
        "help"
      )
      .then((answer) => {
        if (answer === AlertButton.YES) {
          this.exportModel.documentCreationConfirm.emit();
          if (this.type === ReportVO.TYPE_PDF) {
            this.generatePDFReport(properties);
          } else {
            this.generateCSVReport(properties);
          }
        } else {
          this.stopReportTaskIndicators();
        }
      });
  }

  private generatePDFReport(properties: ReportGenerationPropertyVO[]): void {
    this.exportModel.pdfConfirm.next(undefined);
    this.overlayService.requestOverlayShow(OverlayBackground.color);
    this.setPdfWireContours();
    this.representationService.scheduleUpdateForReportGeneration().subscribe({
      next: () => {
        this.onRepresentationUpdateSuccess(properties);
        this.overlayService.requestOverlayHide(OverlayBackground.color);
        this.setDefaultWireContours();
      },
      error: (error) => {
        this.projectReportGeneratedFailure(error);
        this.overlayService.requestOverlayHide(OverlayBackground.color);
        this.setDefaultWireContours();
      }
  });
  }

  private generateCSVReport(properties: ReportGenerationPropertyVO[]): void {
    // TODO: (from flex) probably move this in else-case, so only when current project gets csv exported
    if (!!this.projectModel.currentProject) {
      ArticleContainerUtils.updateProjectWithGeometricData(this.projectModel.currentProject, this.globalContainer);
    }

    if (this.multipleProjectReport) {
      this.reportDataService
        .generateCSVReportForMultipleProjects(
          this.projectIds,
          this.projectGroupIds,
          this.userModel.userProfile,
          this.exportModel.reports,
          properties,
          this.localeResourceManager.currentLanguage.langKey
        )
        .subscribe(
          (data: GenerationResponseVO) => {
            if (!data) {
              this.projectReportGeneratedFailure();
            }
            this.waitForReport(data.contentId);
          },
          (error) => {
            this.projectReportGeneratedFailure(error);
          }
        );
    } else {
      this.reportDataService
        .generateCSVReportForProject(
          this.projectModel.currentProject,
          this.userModel.userProfile,
          this.exportModel.reports,
          properties,
          this.localeResourceManager.currentLanguage.langKey
        )
        .subscribe(
          (data: GenerationResponseVO) => {
            if (!data) {
              this.projectReportGeneratedFailure();
            }
            this.waitForReport(data.contentId);
          },
          (error) => {
            this.projectReportGeneratedFailure(error);
          }
        );
    }
  }

  private getAllReportIds(): string[] {
    const result: string[] = [];
    for (const template of this.exportModel.reports) {
      result.push(template.id);
    }
    return result;
  }

  private getSelectedReportIds(): string[] {
    const result: string[] = [];
    for (const template of this.exportModel.reports) {
      if (template.selected) {
        result.push(template.id);
      }
    }
    return result;
  }

  public onReportsChange(): void {
    let nextButtonEnabled: boolean = false;
    for (const report of this.exportModel.reports) {
      if (report.selected) {
        nextButtonEnabled = true;
        break;
      }
    }
    if (this.type === ReportVO.TYPE_CSV) {
      nextButtonEnabled =
        nextButtonEnabled &&
        !this.exportModel.csvColumnsList.every((item: CSVColumn): boolean => {
          return !(item.visible && item.selected);
        });
    }
    this.exportModel.nextButtonEnabled = nextButtonEnabled;
  }

  private saveReportGenerationProperties(properties: ReportGenerationPropertyVO[]): void {
    ReportGenerationPropertyUtils.updateReportGenerationProperties(this.userModel.userProfile.reportGenerationProperties, properties);
    this.userDataService.saveReportGenerationProperties(this.userModel.userProfile).subscribe();
  }

  public isReportScaleSelectionAvailable(report: ReportVO): boolean {
    if (!report) {
      return false;
    }
    return ReportIdDef.NOT_SCALED_REPORT_IDS.indexOf(report.id) === -1;
  }

  public isReportSubtypeSelectionAvailable(report: ReportVO): boolean {
    if (!report) {
      return false;
    }
    const subtypes = this.getSubtypesByReportId(report.id);
    return ArrayUtils.isNotEmpty(subtypes);
  }

  public getReportSubtypeDataProvider(report: ReportVO): string[] {
    if (!report) {
      return null;
    }
    return this.getSubtypesByReportId(report.id);
  }

  private generateFileName(projectName: string, paperSize: string): string {
    const fileName = projectName + `_` + this.getPaperSizeLabel(paperSize);
    return StringUtils.sanitizeFileName(fileName);
  }

  public waitForReport(contentId: string, projectName?: string, paperSize?: string): void {
    let subscription: Subscription;

    const fileName: string = projectName ? this.generateFileName(projectName, paperSize) : this.getFileName();

    const callback = (id: string) => {
      // handle case when user is logged out
      if (!this.userModel.isUserLoggedIn) {
        clearTimeout(this._contentIdToGenerationProcessMap[id]);
        this.stopReportTaskIndicators();
        if (subscription) {
          subscription.unsubscribe();
        }
        return;
      }

      subscription = this.reportDataService.checkReport(id).subscribe({
        next: (data: GenerationResponseVO) => {
          if (data.contentGenerated) {
            this.reportGeneratedSuccess(data, fileName);
          } else {
            clearTimeout(this._contentIdToGenerationProcessMap[id]);
            this._contentIdToGenerationProcessMap[id] = setTimeout(() => {
              callback(id);
            }, SelectProjectReportService.TIMER_DELAY);
          }
        },
        error: (error) => {
          this.projectReportGeneratedFailure(error);
          clearTimeout(this._contentIdToGenerationProcessMap[id]);
        }
      });
    };

    this._contentIdToGenerationProcessMap[contentId] = setTimeout(() => {
      callback(contentId);
    }, SelectProjectReportService.TIMER_DELAY);
  }

  private onProjectReportSuccess(reports: ReportVO[]): void {
    for (const template of reports) {
      if (template.type === this.exportModel.type) {
        if (this._userSelectedTemplateIds) {
          template.selected = this._userSelectedTemplateIds.indexOf(template.id) >= 0;
        }
        this.exportModel.reports.push(template);
      }
    }
    if (ReportVO.TYPE_PDF === this.exportModel.type) {
      this.applyPDFReportSpecificSettings();
    }
    this.onReportsChange();

    switch (this.exportModel.type) {
      case ReportVO.TYPE_PDF:
        this.projectComponentPresenter.showSelectProjectReport();
        break;
      case ReportVO.TYPE_CSV:
        this.projectComponentPresenter.showSelectCsvReport();
        break;
      default:
        break;
    }
  }

  private onProjectReportFault(): void {
    if (!this.userModel.isUserLoggedIn) {
      return;
    }
    this.alertHelper.showError('global.title.error', 'selectProjectReportPopup.message.load_report_list_failed');
  }

  private onRepresentationUpdateSuccess(properties: ReportGenerationPropertyVO[]): void {
    this.reportDataService
      .generatePDFReport(
        this.projectModel.currentProject,
        this.userModel.userProfile,
        ArrayUtils.isNotEmpty(this.exportModel.reports) ? this.exportModel.reports : [],
        properties,
        this.localeResourceManager.currentLanguage.langKey,
        ReportGenerationPropertyUtils.createCameraParametersByProperties(properties, this.sceneManager)
      )
      .subscribe({
        next: (data: GenerationResponseVO) => {
          if (!data) {
            this.projectReportGeneratedFailure();
          }
          this.waitForReport(data.contentId);
        },
        error: (error) => {
          this.projectReportGeneratedFailure(error);
        }
      });
  }

  public dataStructureGroupChange(value: string): void {
    const ownCustomNumberColumnVisible: boolean = !(this.exportModel.multipleProjectReport && value === this.exportModel.dataStructureGroup[1]);
    if (this.ownerCustomNumberColumnVisible !== ownCustomNumberColumnVisible) {
      this.ownerCustomNumberColumnVisible = ownCustomNumberColumnVisible;

      this.initCSVColumns();
      this.onReportsChange();
    }
  }

  public onCsvModeChange(): void {
    if (this.exportModel.isDiscreteCsvReport) {
      this.exportModel.dataStructureGroupSelected = this.exportModel.dataStructureGroup[0];
      this.exportModel.dataStructureGroupDisabled = true;
    } else {
      this.exportModel.dataStructureGroupDisabled = false;
    }

    this.initCSVColumns();
    this.onReportsChange();
  }

  private reportGeneratedSuccess(data: GenerationResponseVO, filename: string): void {
    this.alertHelper
      .showAlert('main.title.generate_report_finished', 'main.message.generate_report_finished', null, [AlertButton.OK], "info")
      .then(() => {
        const fileName: string = filename ? filename : this.getFileName();
        const fileExtension: FileExtension = this.type === ReportVO.TYPE_CSV ? '.csv' : '.pdf';
        FileUtils.saveBinaryFile(fileName, data.content, fileExtension, true);
        this.stopReportTaskIndicators()
      });
  }

  private projectReportGeneratedFailure(error: any = null): void {
    if (error) {
      if (error.status === 404) {
        this.alertHelper.showError('main.title.error', 'main.message.generate_report_error.404');
      } else {
        this.alertHelper.showError('main.title.error', 'main.message.generate_report_error', {translationParams: [ErrorUtils.getErrorInfo(error).message]});
      }
    } else {
      const param = this.localeResourceManager.getValueByKey('global.message.unexpected_error');
      this.alertHelper.showError('main.title.error', 'main.message.generate_report_error', {translationParams: [param]});
    }
    this.stopReportTaskIndicators();
  }

  private getPdfFileName(): string {
    let fileName: string = this.projectModel.currentProject ? this.projectModel.currentProject.name + `_` : `_`;
    const paperSizeValue: string = PropertyUtils.getStringPropertyValue(this._properties, ReportGenerationPropertyDef.PAPER_SIZE);
    if (this.exportModel.isPrintSettingsAvailable || StringUtils.isEmpty(paperSizeValue)) {
      fileName += this.getPaperSizeLabel(this.exportModel.paperSizeListSelected);
    } else {
      fileName += this.getPaperSizeLabel(paperSizeValue);
    }
    fileName = fileName.replace(':', '-');
    return StringUtils.sanitizeFileName(fileName);
  }

  private getCSVFileName(): string {
    let fileName: string = '';
    if (this.multipleProjectReport) {
      const isAllInOneCSVReport: boolean = PropertyUtils.getBooleanPropertyValue(this._properties, ReportGenerationPropertyDef.IS_ALL_IN_ONE_REPORT);
      fileName = this.localeResourceManager.getValueByKey('selectProjectReportPopup.csv.fileName.' + Number(isAllInOneCSVReport));
    } else {
      fileName = this.projectModel.currentProject ? this.projectModel.currentProject.name : null;
    }
    if (!fileName) {
      fileName = this.projectService.getProjectName(this.projectModel.currentProject);
    }
    return StringUtils.sanitizeFileName(fileName);
  }

  private getFileName(): string {
    return this.type === ReportVO.TYPE_CSV ? this.getCSVFileName() : this.getPdfFileName();
  }

  public getPaperSizeLabel(item: string): string {
    return this.localeResourceManager.getValueByKey(SelectProjectReportService.LOCALE_PREFIX + ReportGenerationPropertyDef.PAPER_SIZE + '.' + item);
  }

  private stopReportTaskIndicators() {
    this.backgroundTasksInfoService.setTaskActive("pdf-export", false);
    this.backgroundTasksInfoService.setTaskActive("csv-export", false);
  }

  private resetModel(): void {
    const model = this.exportModel;
    model.reports = [];
    model.csvColumnsList = [];
    model.type = null;
    model.isPrintSettingsAvailable = null;
    model.nextButtonEnabled = null;
    model.saveSettings = true;
    model.multipleProjectReport = null;
    model.dataStructureGroupSelected = null;
    model.dataStructureGroupDisabled = null;
    model.encodinGroupSelected = null;
    model.paperSizeListSelected = null;
    model.usedNumbersGroupSelected = null;
    model.includeRemarks = null;
    model.useIds = null;
    model.isBorderVisible = null;
    this.ownerCustomNumberColumnVisible = true;
  }

  public openProjectReportPopup(
      type: string,
      multipleProjectReport: boolean = false,
      projectIds: number[] = [],
      projectGroupIds: number[] = [],
      finalizeProjectMode: boolean = false,
      projectFinalizationInfoVO: ProjectFinalizationInfoVO = null
  ): void {
    this.showDialog(type, finalizeProjectMode);
    this.multipleProjectReport = multipleProjectReport;
    this.projectIds = projectIds;
    this.projectGroupIds = projectGroupIds;
    this.projectFinalizationInfo = projectFinalizationInfoVO;
  }
}
